<template>
  <button
    type="button"
    :class="['NavPillButton', { 'is-active': active }]"
    @touchend.prevent="$emit('select')"
    @click="$emit('select')"
  >
    <span class="cell-icon">
      <slot />
    </span>
    <span v-if="label" class="cell-label">{{ label }}</span>
  </button>
</template>

<script setup lang="ts">
export interface NavPillButtonProps {
  /* Rendered under the pill. Omit it to drop the label row entirely. */
  label?: string;
  active?: boolean;
}

withDefaults(defineProps<NavPillButtonProps>(), {
  label: undefined,
  active: false,
});

defineEmits<{ select: [] }>();
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
/* Every knob is read with an inline fallback rather than declared here, so a
   value set on an ancestor still reaches us — a local declaration would win
   over the inherited one and make --nav-cell-size et al. impossible to set. */
.NavPillButton {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nav-label-gap, 0.375rem);
  width: var(--nav-cell-size, 3rem);
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-white-70);
  cursor: pointer;
  touch-action: manipulation;
  transition: color 200ms linear;

  @include hover {
    color: var(--color-white);
  }

  @include press;

  &.is-active {
    color: var(--color-pink);
  }
}

.cell-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: var(--nav-cell-size, 3rem);
}

.cell-label {
  /* Together with the button's gap this fills exactly --screen-nav-label-offset,
     which bottom-anchored siblings use to align with the pill. */
  height: calc(var(--screen-nav-label-offset) - var(--nav-label-gap, 0.375rem));
  line-height: calc(var(--screen-nav-label-offset) - var(--nav-label-gap, 0.375rem));
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--color-white-50);
  white-space: nowrap;
  transition: color 200ms linear;
}

.NavPillButton.is-active .cell-label {
  color: var(--color-pink);
}
</style>
